import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import bsonObjectId from 'bson-objectid';

import { FieldComponentType, IAutocompleteInputFieldValue, IField, IValueChangedEventArgs } from '@azavista/components/shared';
import { AzavistaApiService, ISettingItem, ITeamListItem } from '@azavista/servicelib';
import { TranslationKey } from '../enums';
import { SharedService } from '../shared.service';
import { ICreateNewDashboardComponentData, ICreateNewDashboardComponentResult } from './interfaces';

@Component({
    templateUrl: 'create-new-dashboard-popup.component.html',
})
export class CreateNewDashboardPopupComponent implements OnInit {
    teamField: IField;
    teamFieldValue: IAutocompleteInputFieldValue;
    selectedTeams: ITeamListItem[];
    selectedTeam: ITeamListItem;
    allTeams: ITeamListItem[];
    canShareItem: boolean;
    isNew = false;
    settingItem: ISettingItem;
    duplicateDashboard = false;
    dashboardToDuplicate: ISettingItem;
    dashboards: ISettingItem[];

    constructor(
        public dialogRef: MatDialogRef<CreateNewDashboardPopupComponent, ICreateNewDashboardComponentResult>,
        @Inject(MAT_DIALOG_DATA) public data: ICreateNewDashboardComponentData,
        private apiSvc: AzavistaApiService, private sharedSvc: SharedService
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.settingItem = this.data.settingItem || this.getEmptySettingItem();
        this.dashboards = this.data.dashboards;
        this.canShareItem = this.data.canShareItem;
        this.isNew = !this.data.settingItem;
        this.allTeams = await this.apiSvc.getAllTeams();
        this.teamField = this.createTeamField();
        this.teamFieldValue = this.createTeamFieldValue();
        this.selectedTeams = this.getSelectedTeams() || [];
    }

    getEmptySettingItem(): ISettingItem {
        const emptySettingItem = { shared: false, view_by_all: true, team_ids: [] } as ISettingItem;
        return emptySettingItem;
    }

    getSelectedTeams(): ITeamListItem[] {
        if (!this.settingItem || !this.settingItem.team_ids) {
            return [];
        }
        const filteredTeams = this.allTeams.filter(x => this.settingItem.team_ids.indexOf(x.id) >= 0);
        return filteredTeams;
    }

    createTeamField(): IField {
        const field = {
            id: 'team', name: 'team', editable: true,
            schema: { type: 'object' }, component: FieldComponentType.autocomplete
        } as IField;
        return field;
    }

    createTeamFieldValue(): IAutocompleteInputFieldValue<ITeamListItem> {
        const items = this.allTeams || [];
        const value: IAutocompleteInputFieldValue = {
            displayProperty: 'name', items: items, selected: null, filteredItems: items
        };
        return value;
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        this.settingItem.team_ids = this.selectedTeams.map(x => x.id);
        if (!this.canShareItem) {
            this.removeSharePropertiesFromSettingItem();
        }
        if (this.duplicateDashboard) {
            this.sanitizeDuplicateSettingItem();
        }
        this.dialogRef.close({ settingItem: this.settingItem, isNew: this.isNew, delete: false });
    }

    sanitizeDuplicateSettingItem(): void {
        delete this.settingItem.id;
        delete this.settingItem.created_at;
        delete this.settingItem.updated_at;
    }

    onDelete(): void {
        this.dialogRef.close({ settingItem: this.settingItem, isNew: this.isNew, delete: true });
    }

    removeSharePropertiesFromSettingItem() {
        delete this.settingItem.shared;
        delete this.settingItem.view_by_all;
        delete this.settingItem.team_ids;
    }

    onAddTeamClicked(): void {
        if (!this.selectedTeam) {
            return;
        }
        if (this.selectedTeams.find(x => x.id === this.selectedTeam.id)) {
            return;
        }
        this.selectedTeams.push(this.selectedTeam);
    }

    onRemoveTeamClicked(team: ITeamListItem): void {
        const index = this.selectedTeams.findIndex(x => x.id === team.id);
        this.selectedTeams.splice(index, 1);
    }

    onTeamValueChanged(change: IValueChangedEventArgs): void {
        this.selectedTeam = change.value as ITeamListItem;
    }

    onDashboardToDuplicateSelected(selectEvent: MatSelectChange): void {
        this.settingItem = { ...selectEvent.value };
        this.settingItem.setting.dashboard?.widgets?.forEach((x: any) => x.id = bsonObjectId().toHexString());
        this.settingItem.name = `${this.settingItem.name} ${this.sharedSvc.translate(TranslationKey.copy)}`;
    }

    onDoDuplicate(toggleChange: MatSlideToggleChange): void {
        if (!toggleChange.checked) {
            this.settingItem = this.getEmptySettingItem();
        }
    }
}
